import type React from "react";
import {type ComponentType, memo, type ReactElement, type ReactNode, useMemo} from "react";

import {isReactElement} from "../../react/utils/isReactElement";
import {HStack} from "../ui/hstack";
import type {IIConProps} from "../ui/icon";
import {MenuItemLabel} from "../ui/menu";
import type {ITextProps} from "../ui/text/types";

type Size = "xs" | "sm";

interface Props {
    Icon: ReactElement<unknown> | ComponentType<IIConProps>;
    children: ReactNode;
    size?: Size;
    suffix?: ReactNode;
    iconColor?: "red.500" | "blueGray.400" | "error.600";
    textColor?: "error.600" | "danger.600";
}

const DropdownContent: React.FC<Props> = ({
    children,
    Icon,
    size = "xs",
    iconColor = "blueGray.400",
    textColor,
    suffix
}) => {
    const sizeStyles = useMemo(() => {
        const sizes = {
            xs: {
                iconSize: "xs",
                textSize: "xs"
            },
            sm: {
                iconSize: "sm",
                textSize: "sm"
            }
        } satisfies Record<Size, {iconSize: IIConProps["size"]; textSize: ITextProps["variant"]}>;

        return sizes[size];
    }, [size]);

    const iconNode = isReactElement(Icon) ? (
        Icon
    ) : (
        <Icon color={iconColor} size={sizeStyles.iconSize} />
    );

    return (
        <HStack className="items-center" space="sm" testID="dropdown-content">
            {iconNode}
            <MenuItemLabel color={textColor} size={sizeStyles.textSize}>
                {children}
            </MenuItemLabel>
            {suffix}
        </HStack>
    );
};

export default memo(DropdownContent);
